import "./modal.js";

// Private to this module. Each WizardModal manages a list of these as its
// sub-pages.
class Page {
  constructor(options) {
    this.show = this.show.bind(this);
    this.hide = this.hide.bind(this);
    this.destroy = this.destroy.bind(this);
    this.modal = new Modal(options);
    this.modal.container.detach(); // No need to have the sub modals appearing in the DOM

    if (options.navigationButtons) {
      this.modal.footer.prepend("<");
    }

    this.hide();

    this.setHeader = this.modal.setHeader;
    this.setBody = this.modal.setBody;
    this.content = this.modal.dialog;
    this.element = this.modal.dialog.get(0);
    this.nav = this.modal.footer;
  }

  show() {
    return this.modal.dialog.show();
  }

  hide() {
    return this.modal.dialog.hide();
  }

  destroy() {
    this.modal.dialog.remove(); // We removed this from the modal, so destroy it separately
    return this.modal.destroy();
  }
}

window.WizardModal = class WizardModal {
  constructor(options) {
    this.addPage = this.addPage.bind(this);
    this.hide = this.hide.bind(this);
    this.show = this.show.bind(this);
    this.showNext = this.showNext.bind(this);
    this.showPrevious = this.showPrevious.bind(this);
    this.showPage = this.showPage.bind(this);
    this.getPage = this.getPage.bind(this);
    this.abortCompletion = this.abortCompletion.bind(this);
    this.disableNextPage = this.disableNextPage.bind(this);
    this.enableNextPage = this.enableNextPage.bind(this);
    this.disableCompletion = this.disableCompletion.bind(this);
    this.enableCompletion = this.enableCompletion.bind(this);
    this.buildCompleteButtons = this.buildCompleteButtons.bind(this);
    this.buildCompleteButton = this.buildCompleteButton.bind(this);
    this.addButtonCallback = this.addButtonCallback.bind(this);
    this.isShowing = this.isShowing.bind(this);
    this.destroy = this.destroy.bind(this);
    this.handleKeypress = this.handleKeypress.bind(this);
    this.handleShowPageButtonClick = this.handleShowPageButtonClick.bind(this);
    this.handleModalBeforeHide = this.handleModalBeforeHide.bind(this);
    this.handleModalHide = this.handleModalHide.bind(this);
    this.handleCompleteModalClick = this.handleCompleteModalClick.bind(this);
    this._complete = this._complete.bind(this);
    this._beforeCancel = this._beforeCancel.bind(this);
    this._afterCancel = this._afterCancel.bind(this);
    this._triggerEvent = this._triggerEvent.bind(this);
    this._pageNavigation = this._pageNavigation.bind(this);
    this._navPage = this._navPage.bind(this);
    this.options = jQuery.extend(
      {
        appendTo: "body",
        clickOut: false,
        cancelText: "Cancel",
        previousText: "Back",
        nextText: "Next",
        completeText: "Done",
        completeButtons: undefined, // Can also pass custom complete buttons [{ text: 'Continue without saving', complete: fn }, { text: 'Save and continue', beforeComplete: fn }]
        destroyOnHide: false,
      },
      options,
    );

    this.modal = new Modal(this.options);
    this.pages = [];
    this.pageNumber;
    this.pageContainer = this.modal.dialog.parent().empty();
    this.container = this.modal.container;
    this.completing;

    this.navigationElements = {
      nav: jQuery('<div class="nav">'),
      cancel: jQuery('<button class="nav-cancel nav-button btn" data-behaviour="close-modal"></button>').text(
        this.options.cancelText,
      ),
      pagination: jQuery('<span class="nav-pagination"></span>'),
      previous: jQuery(
        '<button class="nav-previous nav-button btn" data-behaviour="show-previous-modal-page"></button>',
      ).text(this.options.previousText),
      next: jQuery(
        '<button class="nav-next nav-button btn primary" data-behaviour="show-next-modal-page"></button>',
      ).text(this.options.nextText),
      complete: this.buildCompleteButtons(this.options.completeButtons || [{ text: this.options.completeText }]),
      completeDisabled: jQuery('<button class="nav-finish btn primary disabled"></button>'),
    };

    // BEHAVIOUR

    this.modal.container.on("click", '[data-behaviour="show-next-modal-page"]:not(.disabled)', this.showNext);
    this.modal.container.on("click", '[data-behaviour="show-previous-modal-page"]', this.showPrevious);
    this.modal.container.on("click", '[data-behaviour="show-modal-page"]', this.handleShowPageButtonClick);
    this.modal.container.on("click", '[data-behaviour="complete-modal"]:not(.disabled)', this.handleCompleteModalClick);
    this.modal.container.on("keypress", "form input", this.handleKeypress);
    this.modal.container.on("beforeHide", this.handleModalBeforeHide);
    this.modal.container.on("hide", this.handleModalHide);
  }

  // DSL

  addPage(pageOptions) {
    const page = new Page(pageOptions);
    this.pages.push(page);
    this.pageContainer.append(page.content);

    return page;
  }

  hide() {
    this.modal.hide();
    if (this.options.destroyOnHide) {
      return this.modal.destroy();
    }
  }

  show() {
    return this.showPage(1);
  }

  showNext() {
    return this.showPage(this.pageNumber + 1);
  }

  showPrevious() {
    return this.showPage(this.pageNumber - 1);
  }

  showPage(number) {
    const page = this.getPage(number);

    if (!page) {
      return;
    }

    this._triggerEvent("beforePageShow", [page]);
    jQuery(this.pages).each(function () {
      return this.hide();
    });
    page.nav.append(this._pageNavigation(number));
    page.show();
    this.modal.show();
    this.pageNumber = number;
    return this._triggerEvent("pageShow", [page]);
  }

  getPage(number) {
    return this.pages[number - 1];
  }

  // Subscribes `handler` to a wizard event (`beforeComplete`, `complete`, `beforeShow`, `cancel`, …)
  // and returns the wizard. The handler receives the event's data, without the jQuery event.
  on(eventName, handler) {
    jQuery(this).on(eventName, (_event, ...data) => handler(...data));
    return this;
  }

  // Call this function during a beforeComplete callback to cancel the completion
  abortCompletion() {
    return (this.completing = false);
  }

  disableNextPage(text) {
    return this.navigationElements.next.prop("disabled", true).text(text || this.options.nextText);
  }

  enableNextPage() {
    return this.navigationElements.next.prop("disabled", false).text(this.options.nextText);
  }

  disableCompletion(text) {
    return this.navigationElements.complete.prop("disabled", true).text(text || this.options.completeText);
  }

  enableCompletion() {
    return this.navigationElements.complete.prop("disabled", false).text(this.options.completeText);
  }

  buildCompleteButtons(array) {
    let elements = jQuery();
    jQuery.each(
      array.reverse(),
      (
        _,
        buttonConfig, // Reverse because we float the elements so they appear in reverse order
      ) => (elements = elements.add(this.buildCompleteButton(buttonConfig))),
    );

    elements.first().addClass("primary"); // First because that button will become the rightmost button
    return elements;
  }

  buildCompleteButton(buttonConfig) {
    const button = jQuery('<button class="nav-finish btn" data-behaviour="complete-modal"></button>').text(
      buttonConfig.text,
    );
    button.attr("data-completion-button", buttonConfig.text);

    if (buttonConfig.beforeComplete) {
      this.addButtonCallback("beforeComplete", buttonConfig.text, buttonConfig.beforeComplete);
    }

    if (buttonConfig.complete) {
      this.addButtonCallback("complete", buttonConfig.text, buttonConfig.complete);
    }
    return button;
  }

  addButtonCallback(eventName, button, callback) {
    return this.modal.container.on(eventName, (_, btn) => {
      if (btn === button) {
        return callback();
      }
    });
  }

  isShowing() {
    return this.modal.isShowing();
  }

  destroy() {
    jQuery.each(this.pages, function () {
      return this.destroy();
    });
    return this.modal.destroy();
  }

  // EVENT HANDLERS

  handleKeypress(event) {
    // TODO: CLOUIE87 - Confirm what should happen when there are multiple buttons (should they be tabbable?)
    if (event.which === 13) {
      // if enter was pressed
      this._complete();
      return false;
    }
  }

  handleShowPageButtonClick(event) {
    return this.showPage(jQuery(event.currentTarget).data("modal-page"));
  }

  handleModalBeforeHide(event) {
    if (!this.completing) {
      return this._beforeCancel();
    }
  }

  handleModalHide(event) {
    if (!this.completing) {
      return this._afterCancel();
    }
  }

  handleCompleteModalClick(event) {
    return this._complete(jQuery(event.currentTarget).data("completion-button"));
  }

  // PRIVATE

  _complete(button) {
    this.completing = true;

    this._triggerEvent("beforeComplete", button);
    if (this.completing) {
      this.modal.hide();
      this.completing = false;
      return this._triggerEvent("complete", button);
    }
  }

  _beforeCancel() {
    return this._triggerEvent("beforeCancel");
  }

  _afterCancel() {
    return this._triggerEvent("cancel");
  }

  _triggerEvent(eventName, data) {
    jQuery(this).triggerHandler(eventName, data);
    return this.modal.container.trigger(eventName, data);
  }

  _pageNavigation(number) {
    const { nav } = this.navigationElements;
    nav.toggleClass("single-page", this.pages.length === 1);
    nav.children().detach();
    nav.append(this.navigationElements.pagination); // We always append this so it can serve as a spacer for the nav items

    if (this.pages.length > 1) {
      this.navigationElements.pagination.empty();
      jQuery(this.pages).each((index) =>
        this._navPage(index + 1, number === index + 1).appendTo(this.navigationElements.pagination),
      );
    }

    if (number === 1) {
      nav.prepend(this.navigationElements.cancel);
    }

    if (number !== 1) {
      nav.prepend(this.navigationElements.previous);
    }

    if (number < this.pages.length) {
      nav.append(this.navigationElements.next);
    }

    if (number === this.pages.length) {
      nav.append(this.navigationElements.complete);
    }

    return nav;
  }

  _navPage(number, selected) {
    return jQuery('<span class="nav-page" data-behaviour="show-modal-page">')
      .toggleClass("selected", selected)
      .attr("data-modal-page", number);
  }
};
